<template>
    <div class="card card-height">
        <div class="table-responsive">
            <table class="table custom-table-padding">
                <thead>

                    <tr>
                        <th>Photo</th>
                        <th class="patient-details">Patient Details</th>
                        <th class="admission-no">
                            <span class="d-block">Admission No |</span>
                            <span class="d-block">Date & Time</span>
                        </th>
                        <th>Ward/Bed/Cabin</th>
                        <th>
                            <span class="d-block">Consultant</span>
                            <span>(Referred doctor)</span>
                        </th>
                        <th>Admitted By</th>
                        <th class="action-th" colspan="2">Action</th>
                        <th class="action-th" v-if="$route.name !== 'ipd-station'">Statement</th>
                        <th v-if="registrationStatus == 'released'">Print</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="(item, i) in tableItems" :key="i">

                        <td>
                            <img :src=item.patient_photo alt="avatar" class="profile-pic">
                        </td>

                        <td>
                            <div class="col">
                                <strong>
                                    <h4>{{ item.patient_name }} {{ formatPatientInfo(item) }} </h4>
                                </strong>
                                {{ formatPatientAdditionalInfo(item) }}
                            </div>
                        </td>

                        <td>
                            <div class="col">
                                <h5 class="text-bold">{{ item.admission_no }}</h5>
                               <span>{{ item.admission_date }}</span>
                            </div>
                        </td>

                        <td>
                            {{ item.bed }}
                        </td>

                        <td>
                            {{ item.consultant }}
                        </td>

                        <td>
                            {{ item.admitted_by }}
                        </td>
                        <td colspan="2">
                            <div class="action">
                                <button @click="goToDoctorOrder(item)" class="btn btn-primary btn-sm">
                                    <i class="fas fa-plus"></i>
                                    Doctor's order
                                </button>
                            </div>
                        </td>
                        <td v-if="registrationStatus == 'released'">
                            <div class="action">
                                <button class="btn btn-secondary btn-sm ml-2"
                                    @click="goToPrint(item)"
                                >
                                    <i class="fas fa-print"></i>
                                </button>
                            </div>
                        </td>
                        <slot v-if="$route.name !== 'ipd-station'">
                            <td>
                                <div class="action">
                                    <button @click="goToOPDBillList(item.id)" class="btn btn-primary btn-sm">
                                      <i class="fas fa-plus"></i>
                                      Bill
                                    </button>
                                </div>
                            </td>
                            <td>
                                <button class="btn btn-success btn-sm d-flex mx-auto"
                                    @click="goToIPDStatementReport(item.id)"
                                    style="cursor: pointer;"
                                >
                                  <i class="fas fa-file-pdf"></i>
                                </button>
                            </td>
                        </slot>
                    </tr>
                </tbody>
            </table>
        <p v-if="!tableItems.length" class="text-center mt-5">No data</p>
    </div>
</div>
</template>

<script setup>
    import { useRoute, useRouter } from "vue-router";
    import { dateOfBirthConvertToAge } from '@/services/utils/global.js'
    import dischargeOrderHelper from '@/services/utils/pdf/dischargeOrderHelper';
    import handleHospital from '@/services/modules/hospital';
    import { json_string, pluck } from '@/services/utils/global';
    import { head } from 'lodash';
    import { ref, reactive, inject } from 'vue';

    const { generatePdf, formatInvestigations } = dischargeOrderHelper();
    const { fetchSingleDischargeOrder, fetchSingleNote, fetchDischargeStatus } = handleHospital();

    const $route = useRoute();
    const $router = useRouter();
    import { lowerCase } from 'lodash'
    const props = defineProps({
        tableItems: {
            type: Array,
        },
        registrationStatus: {
            type: String
        },
        customizationPdfFields: {
            type: Object,
        }
    })

    const showError = inject('showError');
    const showSuccess = inject('showSuccess');

    const companyInfo = ref({});
    const treatedByDoctors = ref('');
    const admissionInfo = ref(null);
    const serviceDoctorOrder = ref({});
    const composedByDoctor = ref(null);
    const service = ref(null);
    const dischargeModes = reactive([]);
    const newInvestigations = ref([]);
    const dischargeNotes = reactive({
        discharge_note: null,
        rx: null,
        follow_up: null,
        investigation: null,
    });

    const notesOrder = reactive({
        babyNotes: [],
        otNotes: [],
        investigations: []
    });

    const formData = reactive({
        suffering_from: null,
        mode_of_discharge: null,
    });

    const goToDoctorOrder = (item) => {
        const params = $route.params;
        params.serviceId = item.service_general_id;

        const query = $route.query;
        query.admissionId = item.id;

        $router.push({name: 'ipd-doctors-order',
            params: item.service_general_id,
            query: query,
        })
    }

    const goToOPDBillList = (id) => {
        const params = $route.params;
        const query = $route.query;
        params.ipdRegisterId = id;
        query.pageId = params.pageId

        $router.push({name: 'ipd-bill-list',
            params: params,
            query: query,
        })
    }

    const goToIPDStatementReport = (id) => {
        const params = $route.params;
        const query = $route.query;
        query.id = id;

        $router.push({name: 'ipd-statement',
            params: params,
            query: query,
        })
    }

    const formatPatientInfo = (item) => {
        let text = '';
        if(!item.patient_birthday && !item.patient_gender) return text;

        if(item.patient_birthday){
            const { year } = dateOfBirthConvertToAge(item.patient_birthday);
            text =+ year + 'years';
        }

        if(item.patient_birthday && item.patient_gender) text += ', ';

        if(item.patient_gender){
            const gender = lowerCase(item.patient_gender);
            text += `${gender}`;
        }

        return `(${text})`;
    }

    const formatPatientAdditionalInfo = (item) => {
        let text = '';
        if(item.mobile) text += item.mobile;
        if((item.mobile && item.id)) text += ' | ';
        if(item.serial_no) text += item.serial_no;
        return text;
    }

    const goToPrint = async (item) => {
        const prescriptionType = 'discharge_order';
        const serviceId = item.service_general_id;
        const admissionId = item.id;
        const query = getQuery(admissionId);
        
        try {
            // 1. Fetch discharge order
            const dischargeRes = await fetchSingleDischargeOrder(query, serviceId, prescriptionType);
            if (! dischargeRes.status ) return showError(dischargeRes.message);
            service.value = dischargeRes.data.service;
            treatedByDoctors.value = dischargeRes.data.treated_by_doctors;
            setNoteData(dischargeRes.data.dischargeNotes);
            dischargeModes.push(...dischargeRes.data.dischargeModes);
            newInvestigations.value = dischargeRes.data.investigations;
            
            // 3. Fetch admission info
            const admissionRes = await fetchSingleNote(query, admissionId);
            if (! admissionRes.status ) return showError(admissionRes.message);
            admissionInfo.value = admissionRes.data;

            // 4. Format data
            formatDataForPrint();

            // 5. Generate PDF
            generatePdf(
                companyInfo.value,
                admissionInfo.value,
                dischargeNotes,
                formData,
                notesOrder,
                dischargeModes,
                treatedByDoctors.value,
                serviceDoctorOrder.value,
                props.customizationPdfFields
            );
        } catch (error) {
            showError('Something went wrong');
        }
    };

    const formatDataForPrint = () => {
        formData.dischargeDate = new Date(service.value.delivery_date_time);
        setPrintData();
        if(serviceDoctorOrder.value) {
            serviceDoctorOrder.value.composed_service_name = composedByDoctor.value ? composedByDoctor.value.name : '';
        }
    }

    const getQuery = (admissionId) => {
        let query = `?company_id=${$route.params.companyId}`;
        query += `&admission_id=${admissionId}`;
        return query;
    }

    const setNoteData = (notes) => {
        for(let note of notes){
            dischargeNotes[note.value] = note;
        }
    }

    const setOtherNoteData = (other_notes) => {
        for(let noteItem of other_notes){

            let prevNote = dischargeNotes[noteItem.type]
            if(! prevNote ) continue;

            prevNote.id = noteItem.id;
            prevNote.selectedNotes.push(...noteItem.note);
        }
    }

    const setMedicineOrderData = (medicineRx, noteType) => {
        for(let note of medicineRx){

            const noteObj = {
                id: note.id,
                product_id: note.product_id,
                name: note.product.name,
                remarks: note.remarks,
                isSelected: true,
            };

            if(noteType === 'rx'){
                noteObj['dose'] = note.dose;
                noteObj['unit'] = note.unit;
                noteObj['duration'] = note.duration;
                noteObj['madication_status'] = note.madication_status;
            }
            dischargeNotes[noteType].selectedNotes.push(noteObj)
        }
    }

    const setPrintData = () => {
        notesOrder.babyNotes = service.value.ipd_register.baby_notes;
        notesOrder.otNotes = service.value.ipd_register.ot_orders;

        const investigationHistoryNote = head(service.value.ipd_register.notes)
        if(investigationHistoryNote){
            newInvestigations.value = newInvestigations.value.concat(investigationHistoryNote.note)
        }

        if(newInvestigations.value.length){
            setInvestigationData(newInvestigations.value)
        }

        const doctorOrder = service.value.doctor_order;
        if(!doctorOrder) return;

        serviceDoctorOrder.value = doctorOrder;

        if(doctorOrder.composed_by){
            composedByDoctor.value = {
                id: doctorOrder.composed_by,
                name: doctorOrder.composed_service_name
            }
        }

        if(doctorOrder.additional_info){
            setFormData(doctorOrder.additional_info);
        }

        if(doctorOrder.medicine_orders.length){
            setMedicineOrderData(doctorOrder.medicine_orders, 'rx');
        }

        if(doctorOrder.investigation_orders.length){
            setMedicineOrderData(doctorOrder.investigation_orders, 'investigation');
        }

        if(doctorOrder.notes.length){
            setOtherNoteData(doctorOrder.notes);
        }
    }

    const setInvestigationData = async (investigations) => {

        const investigationIds = pluck(investigations, 'id');
        const query = `?company_id=${$route.params.companyId}&investigation_ids=${json_string(investigationIds)}`;

        await fetchDischargeStatus(query).then((res) => {
            if(!res.status) return;
            notesOrder.investigations = formatInvestigations(investigations, res.data);
        })

    }

    const setFormData = (additionalInfo) => {
        formData.mode_of_discharge = additionalInfo.mode_of_discharge;
        formData.suffering_from = additionalInfo.suffering_from
    }
</script>

<style scoped>

.admission-no{
    width: 15%;
}
.patient-details{
    width: 25%;
}
.print-btn{
    margin-right: 5px;
}
.action-th{
    width: 15%;
    text-align: center;
}
.action{
    text-align: center;
}

.profile-pic{
    width: 65px;
}
</style>
